import { useState } from "react";

import { Button, InfoModal, Input, TextArea } from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import {
  withTrackEvent,
  objectTypes,
  actionNames,
  actionPrefixes,
} from "@newsela/snitch";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import slugifyString from "@/utils/slugifyString";
import useCreateTextSet from "@/view/utils/useCreateTextSet";
import {
  cancelButtonCss,
  modalContentWrapperCss,
  modalFooterCss,
  modalRootCss,
  textAreaCss,
} from "./CreateTextSetModal.style";

const ButtonWithTrackEvent = withTrackEvent(Button);

const sharedTracking = {
  objectType: objectTypes.content,
  actionName: actionNames.content,
};

export const errorMessages = {
  title: "A title is required to create a text set",
  description: "A description is required to create a text set",
};

const propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

export const CreateTextSetModal = (props) => {
  const [textSetTitle, setTextSetTitle] = useState("");
  const [textSetTitleTouched, setTextSetTitleTouched] = useState(false);
  const [textSetDescription, setTextSetDescription] = useState("");
  const [textSetDescriptionTouched, setTextSetDescriptionTouched] =
    useState(false);

  const dateNow = new Date();
  const dateNowAsISOString = dateNow.toISOString();

  const payload = {
    title: textSetTitle,
    description: textSetDescription,
    slug: slugifyString(textSetTitle),
    date_shared: dateNowAsISOString,
  };

  const [createNewTextSet] = useCreateTextSet();

  const router = useRouter();

  const navigateToTextSet = ({ textSet }) => {
    const href = {
      pathname: `${MONOLITH_ORIGIN}/subject/other/${textSet.id}`,
    };
    router.push(href, null, { shallow: false });
  };

  const handleNewTextSetCreation = async (e) => {
    e.preventDefault();
    try {
      const { data } = await createNewTextSet(payload);
      navigateToTextSet({ textSet: data });
    } catch (_e) {
      // TODO: Add correct error state.
    }
  };

  const validateCreateTextSet = (e) => {
    setTextSetTitleTouched(true);
    setTextSetDescriptionTouched(true);
    if (textSetTitle !== "" && textSetDescription !== "") {
      handleNewTextSetCreation(e);
    }
  };

  const resetForm = () => {
    setTextSetTitle("");
    setTextSetTitleTouched(false);
    setTextSetDescription("");
    setTextSetDescriptionTouched(false);
  };

  const handleCancel = () => {
    resetForm();
    if (props.onCancel) {
      props.onCancel();
    }
  };

  return (
    <InfoModal
      closeButtonProps={{
        onClick: handleCancel,
      }}
      dataProps={{
        "data-qa-selector": "Create text set modal",
      }}
      labelId="NewTextSet"
      footer={
        <>
          <ButtonWithTrackEvent
            actionPrefix={actionPrefixes.cancel}
            onClick={handleCancel}
            flavor={Button.flavors.tertiary}
            __cssFor={{
              root: cancelButtonCss,
            }}
            {...sharedTracking}
          >
            Cancel
          </ButtonWithTrackEvent>
          <ButtonWithTrackEvent
            actionPrefix={actionPrefixes.initiate}
            onClick={validateCreateTextSet}
            {...sharedTracking}
          >
            Create text set
          </ButtonWithTrackEvent>
        </>
      }
      title="New Text Set"
      __cssFor={{
        footer: modalFooterCss,
        modalContentWrapper: modalContentWrapperCss,
        root: modalRootCss,
      }}
      {...props}
    >
      <form>
        <Input
          helperText="Enter a title"
          inputType={Input.inputTypes.text}
          label="Text set name"
          maxLength={90}
          placeholder="e.g. The American Revolution"
          onBlur={() => {
            if (!textSetTitleTouched) {
              setTextSetTitleTouched(true);
            }
          }}
          onChange={(e) => {
            setTextSetTitle(e.target.value);
          }}
          touched={textSetTitleTouched}
          error={
            (textSetTitleTouched && textSetTitle === "") || !payload.title
              ? errorMessages.title
              : null
          }
        />
        <TextArea
          helperText="Enter a description"
          label="Text set description"
          placeholder="e.g. This is a text set about the American Revolution"
          autoAdjustHeight
          __cssFor={{
            text: textAreaCss,
          }}
          onBlur={() => {
            if (!textSetDescriptionTouched) {
              setTextSetDescriptionTouched(true);
            }
          }}
          onChange={(e) => {
            setTextSetDescription(e.target.value);
          }}
          touched={textSetDescriptionTouched}
          error={
            (textSetDescriptionTouched && textSetDescription === "") ||
            !payload.description
              ? errorMessages.description
              : null
          }
        />
      </form>
    </InfoModal>
  );
};

CreateTextSetModal.propTypes = propTypes;
export default CreateTextSetModal;
